import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import FilterPanelCheckboxGroup, {
  type FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'

const GIFT_FEATURED_VALUE: Experiences.FeaturedFilter = 'is-gift'

interface Props {
  giftingAvailableCount?: number
  hideCounts?: boolean
}

function ExperienceSearchGiftInspirationFilter({
  giftingAvailableCount,
  hideCounts = false,
}: Props) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()

  const selectedFeatured = useMemo(
    () => searchFilters.featured ?? [],
    [searchFilters.featured],
  )
  const giftLabel = intl.formatMessage({
    id: 'kJvS7n7U0zzKef7vg4ju',
    defaultMessage: 'Gift inspiration',
    description: 'Recommended filter option for gift inspiration in experience search',
  })
  const isSelected = selectedFeatured.includes(GIFT_FEATURED_VALUE)

  const items = useMemo<Array<FilterPanelCheckItem>>(() => ([{
    value: GIFT_FEATURED_VALUE,
    label: giftLabel,
    checked: isSelected,
    count: giftingAvailableCount,
    hideCount: hideCounts,
    disabled: giftingAvailableCount === 0,
  }]), [giftLabel, giftingAvailableCount, hideCounts, isSelected])

  const onToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== GIFT_FEATURED_VALUE) {
      return
    }

    const selected = selectedFeatured.includes(GIFT_FEATURED_VALUE)
    trackSearchFilter({
      label: giftLabel,
      type: 'featured',
      value: selected ? 'unselected' : 'selected',
    })
    const next = selected
      ? selectedFeatured.filter((value) => value !== GIFT_FEATURED_VALUE)
      : [...selectedFeatured, GIFT_FEATURED_VALUE]
    setSearchFilters({
      ...searchFilters,
      featured: next.length > 0 ? next : undefined,
    })
  }, [giftLabel, searchFilters, selectedFeatured, setSearchFilters, trackSearchFilter])

  return (
    <FilterPanelCheckboxGroup
      name="featured-gift"
      items={items}
      onChange={onToggle}
    />
  )
}

export default ExperienceSearchGiftInspirationFilter
